import React, { useEffect } from 'react';
import type { ComponentType } from 'react';
import {
  Link as ReactRouterLink,
  NavLink as ReactRouterNavLink,
  useInRouterContext,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

type NavigateOptions = {
  replace?: boolean;
  state?: any;
};

type NavigateFunction = (
  target: any,
  options?: NavigateOptions | boolean
) => any;

let registeredNavigate: NavigateFunction | null = null;

const normalizeTarget = (target: any) => {
  if (typeof target !== 'string') return target;

  try {
    const url = new URL(target, window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    return target;
  }
};

const fallbackNavigate = (
  target: any,
  options: NavigateOptions | boolean = {}
) => {
  if (typeof target === 'number') {
    window.history.go(target);
    return;
  }

  const normalizedTarget = normalizeTarget(target);
  const normalizedOptions =
    options === true || options === false ? { replace: options === true } : options;
  const method = normalizedOptions.replace ? 'replaceState' : 'pushState';
  window.history[method](normalizedOptions.state, '', normalizedTarget);
  window.dispatchEvent(
    new PopStateEvent('popstate', { state: normalizedOptions.state })
  );
};

export const navigate: NavigateFunction = (
  target,
  options = {}
) => {
  const normalizedOptions =
    options === true || options === false ? { replace: options === true } : options;

  if (registeredNavigate) {
    return registeredNavigate(normalizeTarget(target), normalizedOptions);
  }

  return fallbackNavigate(target, normalizedOptions);
};

export const registerNavigate = (navigateFunction: NavigateFunction) => {
  registeredNavigate = navigateFunction;
  return () => {
    if (registeredNavigate === navigateFunction) {
      registeredNavigate = null;
    }
  };
};

export const RouterAdapter = () => {
  const navigateFunction = useNavigate();

  useEffect(() => registerNavigate(navigateFunction), [navigateFunction]);

  return null;
};

type RouteComponentProps = {
  component: ComponentType<any>;
  [key: string]: any;
};

/**
 * Preserves the route props that Reach injected into existing class-based
 * screens while the actual route matching is handled by React Router.
 */
export const RouteComponent = ({ component: Component, ...props }: RouteComponentProps) => {
  const params = useParams();
  const location = useLocation();

  return <Component {...params} location={location} {...props} />;
};

const getHref = (to: any): string =>
  typeof to === 'string' ? to : to?.pathname || '#';

type AdapterLinkProps = {
  to: any;
  replace?: boolean;
  state?: any;
  className?: any;
  [key: string]: any;
};

export const Link = ({ to, replace, state, ...props }: AdapterLinkProps) => {
  const inRouter = useInRouterContext();

  if (inRouter) {
    return <ReactRouterLink to={to} replace={replace} state={state} {...props} />;
  }

  return <a href={getHref(to)} {...props} />;
};

export const NavLink = ({
  to,
  replace,
  state,
  className,
  ...props
}: AdapterLinkProps) => {
  const inRouter = useInRouterContext();

  if (inRouter) {
    return (
      <ReactRouterNavLink
        to={to}
        replace={replace}
        state={state}
        className={className}
        {...props}
      />
    );
  }

  const href = getHref(to);
  const isActive =
    window.location.pathname === href ||
    window.location.pathname.startsWith(`${href}/`);
  const resolvedClassName =
    typeof className === 'function'
      ? className({ isActive, isPending: false })
      : className;

  return <a href={href} className={resolvedClassName} {...props} />;
};
